<template>
    <div class="leaflet-map-container" style="position: relative; z-index: 0; height: 100%;">
        <LeafletMapLoader
            ref="mapLoader"
            v-if="renderComponent"
            :mapConfig="mapConfig"
            :heatMap="heatMapUrl"
            :project="project"
            :active="active"
        >
            <template slot-scope="{ map, bounds }">
                <LeafletMarkerWithLaeq
                    v-for="marker in devices"
                    :key="`${marker._id}-${getDeviceInfo(marker.deviceId) ? 'loaded' : 'loading'}`"
                    :marker="marker"
                    :label="marker.name"
                    :deviceInfo="getDeviceInfo(marker.deviceId)"
                    :map="map"
                    :bounds="bounds"
                    @selectMarker="selectMarker(...arguments)"
                />
            </template>
        </LeafletMapLoader>
        <v-overlay
            :absolute="true"
            :value="overlay"
            :dark="false"
        >
            <v-card
                class="ma-3"
                max-width="300"
                min-width="300"
            >
                <v-list-item two-line>
                <v-list-item-content>
                    <v-list-item-title class="headline">
                        {{deviceInfo.name}}
                        <v-icon
                            v-show="isDisconnected && false"
                            color="red darken-2"
                        >
                            mdi-lan-disconnect
                        </v-icon>
                        
                        <v-btn
                            class="float-md-right"
                            @click="overlay = false"
                            icon
                        >
                            <v-icon>mdi-close</v-icon>
                        </v-btn>
                    </v-list-item-title>
                    <v-list-item-subtitle>{{deviceInfo.ts | dateFormat}}</v-list-item-subtitle>
                </v-list-item-content>
                </v-list-item>

                <v-divider></v-divider>

                <v-card-actions >
                    <v-row align="center" justify="center">
                        <v-btn text @click="goSensor(deviceInfo.deviceId)" v-show="false"> Sensor </v-btn>
                        <v-btn text @click="goLaeq(deviceInfo.deviceId)" :disabled="!hasLaeq(deviceInfo.deviceId)"> Sonido </v-btn>
                        <v-btn text disabled> Log </v-btn>
                    </v-row>
                </v-card-actions>
            </v-card>
        </v-overlay>
    </div>
</template>

<script>
	import moment from 'moment'
    import LeafletMapLoader from '@/components/GoogleMap/LeafletMapLoader'
    import LeafletMarkerWithLaeq from '@/components/GoogleMap/LeafletMarkerWithLaeq'
    import { mapSettings } from '@/constants/mapSettings.js'

    const EXCLUDED_DASHBOARD_DEVICE_ID = 'device043'

    export default {
        props: {
            heatMap: Boolean,
            active: {
                type: Boolean,
                default: true
            }
        },
        data: () => ({
            overlay: false,
            selectDevice: null,
            isDisconnected: false,
            deviceInfo: {
                name: '',
                deviceId: ''
            },
            renderComponent: false,
            center: {
                lat: -36.835939888115604,
                lng: -73.07160263961296
            },
            heatMapUrl: '',
            pollingInterval: null,
            lastUpdate: null,
            currentTime: new Date(),
            timeUpdateInterval: null
        }),
        
        computed: {
            mapConfig () {
                return {
                    ...mapSettings,
                    center: this.center
                }
            },
            
            devices () {
					return this.$store.getters.devices.filter( e => {
                    return (
                        e.projectId == this.project &&
                        e.deviceId !== EXCLUDED_DASHBOARD_DEVICE_ID &&
                        Object.hasOwnProperty.call(e, "location")
                    )
                })
            },

            project () {
                return this.$store.getters.project
            },
            
            projects () {
                return this.$store.getters.projects
            },

            projectInfo () {
                return this.projects.find(e => e.projectId === this.project)
            },

            devicesInfo () {
                return this.$store.getters.devicesInfo
            },

            drawer() {
                return this.$store.getters.drawer
            },

            // Obtener el timestamp más reciente de todos los dispositivos
            latestDataTimestamp() {
                if (!this.devicesInfo || this.devicesInfo.length === 0) {
                    return null
                }

                const timestamps = this.devicesInfo
                    .filter(d => d.ts)
                    .map(d => new Date(d.ts).getTime())

                if (timestamps.length === 0) return null

                const latestTime = Math.max(...timestamps)
                return new Date(latestTime)
            },

            updateIconColor() {
                if (!this.latestDataTimestamp) return 'grey'

                const minutesDiff = (this.currentTime - this.latestDataTimestamp) / 1000 / 60

                if (minutesDiff <= 5) return 'success'
                if (minutesDiff <= 15) return 'warning'
                return 'error'
            },

            updateIcon() {
                if (!this.latestDataTimestamp) return 'mdi-clock-alert-outline'

                const minutesDiff = (this.currentTime - this.latestDataTimestamp) / 1000 / 60

                if (minutesDiff <= 5) return 'mdi-check-circle'
                if (minutesDiff <= 15) return 'mdi-alert-circle'
                return 'mdi-close-circle'
            }
        },

        methods: {
            getDeviceInfo(deviceId) {
                return this.$store.getters.deviceInfo(deviceId)
            },

            async updateDevicesInfo() {
                const promises = this.devices.map(device => {
                    return this.$store.dispatch('pullDeviceLastLaeq', device.deviceId)
                })
                await Promise.all(promises)
                this.lastUpdate = new Date()
            },

            refreshMapLayout(fitBounds = false) {
                if (!this.renderComponent || !this.$refs.mapLoader) return
                this.$refs.mapLoader.scheduleMapRefresh({ fitBounds })
            },
            selectMarker(marker) {
                this.goLaeq(marker.deviceId)
            },

            goSensor (id) {
				this.$router.push('/sensor/' + id)
			},

			goLaeq (id) {
				this.$router.push('/laeq/' + id)
            },
            
            hasLaeq (id) {
                return this.$store.getters.devicesHasLaeq(id)
			},

            getCenter ( data ) {
                if (!data || data.length === 0) {
                    this.renderComponent = true
                    return
                }

                let sumLat = 0, sumLng = 0
                const initProject = data[0].projectId
                const centerProject = this.project === 'all' ? initProject : this.project

                let deviceProject = data.filter(e => {
                    return (
                        e.projectId === centerProject &&
                        e.deviceId !== EXCLUDED_DASHBOARD_DEVICE_ID &&
                        Object.hasOwnProperty.call(e, "location")
                    )
                })

                for (let i = 0; i < deviceProject.length; i++) {
                    const element = deviceProject[i]
                    sumLat += element.location.lat
                    sumLng += element.location.lng
                }

                this.renderComponent = true

                if (deviceProject.length > 0) {
                    this.center = {
                        lat: sumLat / deviceProject.length,
                        lng: sumLng / deviceProject.length
                    }
                }
            }
        },

        watch: {
            devices (val) {
                this.getCenter( val )

                if ( this.heatMap && this.projectInfo.info !== undefined )
                    this.heatMapUrl = this.projectInfo.info.kml

                if (val && val.length > 0) {
                    this.updateDevicesInfo()
                }

                this.$nextTick(() => {
                    this.refreshMapLayout(true)
                    setTimeout(() => this.refreshMapLayout(true), 320)
                })
            },

            project () {
                // Proyecto cambiado, actualizar datos i
                this.$nextTick(() => {
                    if (this.devices && this.devices.length > 0) {
                        this.updateDevicesInfo()
                    }
                    this.refreshMapLayout(true)
                    setTimeout(() => this.refreshMapLayout(true), 320)
                })
            },

            drawer() {
                this.$nextTick(() => {
                    this.refreshMapLayout()
                    setTimeout(() => this.refreshMapLayout(), 320)
                })
            },

            active(newVal) {
                if (!newVal) return
                this.$nextTick(() => {
                    this.refreshMapLayout(true)
                    setTimeout(() => this.refreshMapLayout(true), 320)
                })
            }
        },

        filters : {
			dateFormat (val) {
				if (!moment(val).isValid())
					return val

				return moment(val).format('LLL')
			},
        },
        
        components: {
            LeafletMapLoader,
            LeafletMarkerWithLaeq
        },

        created () {
            moment.locale('es-us')

            if ( this.heatMap && this.projectInfo.info !== undefined )
                this.heatMapUrl = this.projectInfo.info.kml

            this.getCenter (this.devices)
        },

        mounted() {
            this.updateDevicesInfo()

            this.pollingInterval = setInterval(() => {
                this.updateDevicesInfo()
            }, 60000) 

            this.timeUpdateInterval = setInterval(() => {
                this.currentTime = new Date()
            }, 1000)

            this.$nextTick(() => {
                this.refreshMapLayout(true)
                setTimeout(() => this.refreshMapLayout(true), 400)
            })
        },

        beforeDestroy() {
            if (this.pollingInterval) {
                clearInterval(this.pollingInterval)
                this.pollingInterval = null
            }

            if (this.timeUpdateInterval) {
                clearInterval(this.timeUpdateInterval)
                this.timeUpdateInterval = null
            }

        }
    }
</script>
